import { yupResolver } from '@hookform/resolvers/yup';
import { FormControlLabel, Checkbox, Box, Grid, DialogContent } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  indexVendorLists,
  IVendorLists,
  VendorListContentType,
  VendorListType,
  getVendorList,
} from 'wisestep-api';
import {
  ControlledTextInput,
  FormWizard,
  WsButton,
  WsButtonGroup,
  WsDialog,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import * as yup from 'yup';

import { WsTeamsFilter } from '../../../../components/filters/components';
import RecruiterSuggestions from '../../../../components/filters/components/RecruiterSuggestions';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext';
import { AddVendors } from '../addVendor';

const schema = yup
  .object()
  .shape({
    name: yup.string().required('Name is required'),
    contentType: yup.string().required('Content Type is required'),
    type: yup.string().required('Access Type is required'),
  })
  .required('required');

const vendorListDefaultValues: IVendorLists = {
  name: '',
  canSendEmailsToAllVendors: true,
  contentType: VendorListContentType.Jobs,
  sharedToRecruiters: [],
  sharedToTeams: [],
  type: VendorListType.Private,
};

const AddVendorList = () => {
  const navigate = useNavigate();
  const [isShared, setIsShared] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [params] = useSearchParams();
  const [vendorList, setVendorList] = useState<IVendorLists>();
  const workspaceContext = useWorkspaceContext();
  const formOptions = {
    resolver: yupResolver(schema),
    defaultValues: params.get('id') ? vendorList : vendorListDefaultValues,
  };
  const methods = useForm(formOptions);

  const onFormSubmit = async () => {
    if (params.get('id')?.length) {
      setOpen(true);
    } else {
      saveVendorList();
    }
  };
  const saveVendorList = async () => {
    vendorListDefaultValues.name = methods.watch('name');
    vendorListDefaultValues.sharedToTeams = methods.watch('sharedToTeams');
    vendorListDefaultValues.id = params.get('id') ?? '';
    vendorListDefaultValues.workspaceId = workspaceContext.id ?? '';
    vendorListDefaultValues.sharedToRecruiters = methods.watch('sharedToRecruiters');
    vendorListDefaultValues.contentType =
      methods.watch('contentType') === 'Jobs & Candidates'
        ? 'JobsAndCandidates'
        : methods.watch('contentType');
    vendorListDefaultValues.type = methods.watch('type');
    const response = await indexVendorLists(vendorListDefaultValues);
    setOpen(false);
    setVendorList(response.data);
    setTabIndex(1);
  };
  const menuItems = [
    {
      id: 0,
      label: 'Vendor List Details',
    },
    {
      id: 1,
      label: 'Add Vendors',
    },
  ];

  useEffect(() => {
    const fetchVendorList = async () => {
      if (params.get('id')) {
        const response = await getVendorList(params.get('id') ?? '');
        const res = response.data;
        if (res) {
          setVendorList(res);
          methods.reset(res);
        }
      }
    };

    fetchVendorList();
  }, [methods, params]);
  return (
    <FormWizard
      title={tabIndex === 0 ? 'Add Vendor List' : 'Add Vendors'}
      menuItems={menuItems}
      currentStep={tabIndex}
      onArrowButtonClick={() => navigate('/vendors/')}
    >
      <FormProvider {...methods}>
        {tabIndex === 0 ? (
          <Grid component="form" noValidate onSubmit={methods.handleSubmit(() => onFormSubmit())}>
            <ControlledTextInput
              labelText="Vendor List Name"
              name="name"
              placeholder="Eg: Geomode UK"
              variant="outlined"
              required
              size="small"
              fullWidth
              defaultValue={vendorList?.name ?? ''}
            />
            <Box py={2}>
              <WsButtonGroup
                inputTitle="Content Type"
                buttonsList={['Jobs', 'Candidates', 'Jobs & Candidates']}
                selectedIndexValue={(index: number, value: string) =>
                  methods.setValue('contentType', value)
                }
                defaultValue={
                  params.get('contentType') == 'Jobs'
                    ? 0
                    : params.get('contentType') == 'Candidates'
                    ? 1
                    : 2
                }
              />
              <WsTypography variant={WsTypoVariants.TITLE}>{vendorList?.contentType}</WsTypography>
            </Box>
            <WsButtonGroup
              inputTitle="Select who can see the list"
              buttonsList={['Private', 'Public', 'Shared']}
              selectedIndexValue={(index: number, value: string) => [
                methods.setValue('type', value),
                index === 2 && value === 'Shared' ? setIsShared(true) : setIsShared(false),
              ]}
              defaultValue={
                params.get('type') == 'Private' ? 0 : params.get('type') == 'Shared' ? 2 : 1
              }
            />
            {isShared ? (
              <Box pt={2}>
                <WsTypography>Share with</WsTypography>
                <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                  <Grid item xs={1}>
                    <WsTypography>Teams</WsTypography>
                  </Grid>
                  <Grid xs={11}>
                    <WsTeamsFilter
                      name="sharedToTeams"
                      multiple={true}
                      defaultValue={vendorList?.sharedToTeams}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                  <Grid item xs={1}>
                    <WsTypography>Users</WsTypography>
                  </Grid>
                  <Grid xs={11}>
                    <RecruiterSuggestions
                      name="sharedToRecruiters"
                      multiple={true}
                      defaultValue={vendorList?.sharedToRecruiters}
                    />
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <></>
            )}
            <FormControlLabel
              sx={{ pt: 2 }}
              control={
                <Checkbox
                  value="canSendEmailsToAllVendors"
                  name="canSendEmailsToAllVendors"
                  onClick={() => [
                    (vendorListDefaultValues.canSendEmailsToAllVendors =
                      !vendorListDefaultValues.canSendEmailsToAllVendors),
                  ]}
                  required
                />
              }
              label=" I agree that all the vendors added to this list have opted to receive emails from me
                (or my organization) at some point."
            />
            <Box display="flex" justifyContent="flex-end" pt={6}>
              <WsButton type="submit" variant="contained" size="large">
                Next
              </WsButton>
            </Box>
          </Grid>
        ) : (
          <Box>{vendorList && <AddVendors vendorList={vendorList} />}</Box>
        )}
      </FormProvider>
      <WsDialog size="medium" openDialog={open}>
        <DialogContent>
          <Grid container justifyContent="space-between" spacing={2} p={1}>
            <Grid item display="flex" justifyContent="center">
              <WsTypography variant={WsTypoVariants.TITLE}>
                Are you sure you want to change list details?
              </WsTypography>
            </Grid>
            <Grid item columnGap={2} display="flex">
              <WsButton
                onClick={() => setOpen(false)}
                type="button"
                size="large"
                variant="outlined"
              >
                No
              </WsButton>
              <WsButton
                onClick={() => saveVendorList()}
                type="button"
                size="large"
                variant="contained"
              >
                Yes
              </WsButton>
            </Grid>
          </Grid>
        </DialogContent>
      </WsDialog>
    </FormWizard>
  );
};

export default AddVendorList;
